import { useState, useCallback, useRef } from "react";
import {
  fetchOrderDetailMock,
  fetchOrdersMock,
} from "../../mocks/ordersMock";
import type {
  OrderDetail,
  OrderListFilters,
  OrderListItem,
} from "../../types/orders";
import {
  EMPTY_ORDER_FILTERS,
  ORDER_LIST_URL,
  ORDER_LOAD_ERROR_MESSAGE,
  appendOrderListFilterParams,
  filterOrdersByStatus,
  mapOrderListResponse,
  orderListItemToDetail,
  resolveOrderLoadError,
  translateOrderMessage,
} from "../../types/orders";
import {
  EXPORT_MAX_ROWS,
  fetchAllPagesForExport,
  type FetchAllPagesForExportResult,
} from "../../utils/fetchAllPagesForExport";

/**
 * Page size used by the exact count. The count only needs each page's
 * length, so it does not have to match the table size.
 */
const FILTERED_COUNT_PAGE_SIZE = 100;
/**
 * Safety bound for the last-page search: 1,000 pages × 100 = 100k rows, far
 * above what the backend's 2-month window holds, so it is never hit in
 * practice.
 */
const FILTERED_COUNT_MAX_PAGES = 1_000;

interface UseGetOrdersReturn {
  orders: OrderListItem[] | null;
  loading: boolean;
  error: string | null;
  /**
   * `totalElements` from list-orders. Only valid for the unfiltered view:
   * the backend computes it from a fixed status subset and ignores the
   * `status`/search filters (verified 2026-09-23: `status=pending-create-order`
   * still reported 21,709). Use `filteredCount` for narrowed lists.
   */
  totalElements: number;
  /** Exact count of rows matching the last `fetchFilteredCount` filters, or null while unknown/loading. */
  filteredCount: number | null;
  /** True only if the count hit FILTERED_COUNT_MAX_PAGES without finding the last page. */
  filteredCountTruncated: boolean;
  filteredCountLoading: boolean;
  detail: OrderDetail | null;
  detailLoading: boolean;
  detailError: string | null;
  fetchOrders: (
    page: number,
    size: number,
    filters?: OrderListFilters
  ) => Promise<void>;
  /**
   * Fetches up to maxRows matching filters without mutating the visible list.
   * Used by Excel export.
   */
  fetchOrdersForExport: (
    filters?: OrderListFilters,
    maxRows?: number
  ) => Promise<FetchAllPagesForExportResult<OrderListItem>>;
  /**
   * Exact row count for `filters`, found by searching for the last page
   * (see countOrdersByLastPage), because `totalElements` ignores the
   * status/search filters. Not used for the unfiltered view.
   */
  fetchFilteredCount: (filters: OrderListFilters) => Promise<void>;
  /**
   * Opens detail from the list row (no GET /order/{id} in DFT contract).
   * Pass the list item when available; otherwise looks up by id in last fetch.
   */
  openOrderDetail: (
    orderVTEXId: string,
    listItem?: OrderListItem | null
  ) => void;
  clearDetail: () => void;
}

const shouldUseMockOrders = (): boolean =>
  process.env.REACT_APP_USE_ORDERS_MOCK === "true";

const authHeaders = (token: string): HeadersInit => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${token}`,
});

const getAccessToken = (): string => {
  // Token is stored via setItem() as JSON.stringify(jwt).
  const tokenStorage = localStorage.getItem("token");
  if (!tokenStorage) {
    throw new Error(
      "Token de acceso no encontrado. Inicia sesión primero."
    );
  }
  try {
    const parsed: unknown = JSON.parse(tokenStorage);
    if (typeof parsed === "string" && parsed.trim()) {
      return parsed.trim();
    }
  } catch {
    // Fallback if token was stored without JSON quotes.
  }
  const stripped = tokenStorage.replace(/^["']|["']$/g, "").trim();
  if (!stripped) {
    throw new Error(
      "Token de acceso no encontrado. Inicia sesión primero."
    );
  }
  return stripped;
};

/** §3.2: page, size, fromDate, toDate, orderVTEXId, orderSIESAId, total, status */
const buildListUrl = (
  page: number,
  size: number,
  filters: OrderListFilters
): string => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  appendOrderListFilterParams(params, filters);
  return `${ORDER_LIST_URL}?${params.toString()}`;
};

/**
 * Single list page request. Does not touch React state.
 */
const requestOrdersPage = async (
  page: number,
  size: number,
  filters: OrderListFilters,
  signal?: AbortSignal
): Promise<{ content: OrderListItem[]; totalElements: number }> => {
  if (shouldUseMockOrders()) {
    const data = fetchOrdersMock(page, size, filters);
    return {
      content: data.content,
      totalElements: data.totalElements,
    };
  }

  const token = getAccessToken();
  const response = await fetch(buildListUrl(page, size, filters), {
    method: "GET",
    headers: authHeaders(token),
    signal,
  });

  if (!response.ok) {
    throw new Error(await resolveOrderLoadError(response));
  }

  const payload = await response.json();
  const data = mapOrderListResponse(payload);
  return {
    content: data.content,
    totalElements: data.totalElements,
  };
};

interface OrderCountResult {
  count: number;
  truncated: boolean;
}

/**
 * Exact row count for `filters` without downloading every row: `content[]`
 * honors all filters, so total = (lastPage − 1) × pageSize + rows on the
 * last page. The last page is found by probing pages 1, 2, 4, 8… until one
 * is short, then binary-searching between the last full and first short
 * page — ~16 requests for 23k rows instead of one request per page.
 *
 * ASSUMPTION: a page past the end returns `content: []` (not an error).
 * Rows may enter or leave the filter mid-count, so the total can be off by
 * a row or two; acceptable for an operations dashboard.
 */
const countOrdersByLastPage = async (
  filters: OrderListFilters,
  pageSize: number,
  signal?: AbortSignal
): Promise<OrderCountResult> => {
  const pageLengths = new Map<number, number>();
  const probe = async (page: number): Promise<number> => {
    const known = pageLengths.get(page);
    if (known !== undefined) return known;
    const { content } = await requestOrdersPage(page, pageSize, filters, signal);
    pageLengths.set(page, content.length);
    return content.length;
  };

  const firstPageLength = await probe(1);
  if (firstPageLength < pageSize) {
    return { count: firstPageLength, truncated: false };
  }

  // Exponential phase — invariant: page `lastFull` is full.
  let lastFull = 1;
  let firstShort = 2;
  while ((await probe(firstShort)) === pageSize) {
    lastFull = firstShort;
    if (firstShort >= FILTERED_COUNT_MAX_PAGES) {
      return { count: lastFull * pageSize, truncated: true };
    }
    firstShort = Math.min(firstShort * 2, FILTERED_COUNT_MAX_PAGES);
  }

  // Binary phase — `lastFull` is full, `firstShort` has fewer rows (or none).
  while (firstShort - lastFull > 1) {
    const mid = Math.floor((lastFull + firstShort) / 2);
    if ((await probe(mid)) === pageSize) lastFull = mid;
    else firstShort = mid;
  }

  return {
    count: lastFull * pageSize + (pageLengths.get(firstShort) ?? 0),
    truncated: false,
  };
};

const useGetOrders = (): UseGetOrdersReturn => {
  const [orders, setOrders] = useState<OrderListItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalElements, setTotalElements] = useState(0);
  const [filteredCount, setFilteredCount] = useState<number | null>(null);
  const [filteredCountTruncated, setFilteredCountTruncated] = useState(false);
  const [filteredCountLoading, setFilteredCountLoading] = useState(false);
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const countRequestIdRef = useRef(0);
  const countAbortControllerRef = useRef<AbortController | null>(null);
  const ordersRef = useRef<OrderListItem[] | null>(null);
  ordersRef.current = orders;

  const fetchOrders = useCallback(
    async (
      page: number,
      size: number,
      filters: OrderListFilters = EMPTY_ORDER_FILTERS
    ): Promise<void> => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);

      try {
        const data = await requestOrdersPage(page, size, filters);
        if (requestId !== requestIdRef.current) return;
        setOrders(filterOrdersByStatus(data.content, filters.status));
        setTotalElements(data.totalElements);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setOrders(null);
        setTotalElements(0);
        setError(
          translateOrderMessage(
            err instanceof Error ? err.message : "",
            ORDER_LOAD_ERROR_MESSAGE
          )
        );
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    []
  );

  const fetchOrdersForExport = useCallback(
    async (
      filters: OrderListFilters = EMPTY_ORDER_FILTERS,
      maxRows: number = EXPORT_MAX_ROWS
    ): Promise<FetchAllPagesForExportResult<OrderListItem>> => {
      try {
        const result = await fetchAllPagesForExport<OrderListItem>({
          maxRows,
          fetchPage: (page, size) => requestOrdersPage(page, size, filters),
        });
        return {
          ...result,
          items: filterOrdersByStatus(result.items, filters.status),
        };
      } catch (err) {
        throw new Error(
          translateOrderMessage(
            err instanceof Error ? err.message : "",
            ORDER_LOAD_ERROR_MESSAGE
          )
        );
      }
    },
    []
  );

  const fetchFilteredCount = useCallback(
    async (filters: OrderListFilters): Promise<void> => {
      // Cancel a still-running count for the previous filters so a status
      // change does not keep paying for the superseded query.
      countAbortControllerRef.current?.abort();
      const abortController = new AbortController();
      countAbortControllerRef.current = abortController;

      const requestId = ++countRequestIdRef.current;
      setFilteredCountLoading(true);
      setFilteredCount(null);
      setFilteredCountTruncated(false);

      try {
        const result = await countOrdersByLastPage(
          filters,
          FILTERED_COUNT_PAGE_SIZE,
          abortController.signal
        );
        if (requestId !== countRequestIdRef.current) return;
        setFilteredCount(result.count);
        setFilteredCountTruncated(result.truncated);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (requestId !== countRequestIdRef.current) return;
        setFilteredCount(null);
        setFilteredCountTruncated(false);
      } finally {
        if (requestId === countRequestIdRef.current) {
          setFilteredCountLoading(false);
        }
      }
    },
    []
  );

  const openOrderDetail = useCallback(
    (orderVTEXId: string, listItem?: OrderListItem | null): void => {
      setDetailLoading(true);
      setDetailError(null);
      setDetail(null);

      try {
        if (shouldUseMockOrders()) {
          const data = fetchOrderDetailMock(orderVTEXId);
          if (!data) {
            throw new Error("No se encontró el detalle de la orden.");
          }
          setDetail(data);
          return;
        }

        const fromList =
          listItem ??
          ordersRef.current?.find((o) => o.orderVTEXId === orderVTEXId) ??
          null;

        if (!fromList) {
          throw new Error(
            "No se encontró la orden en el listado. Actualiza e inténtalo de nuevo."
          );
        }

        // No detail endpoint in DFT — use list row as detail source.
        setDetail(orderListItemToDetail(fromList));
      } catch (err) {
        setDetailError(
          translateOrderMessage(
            err instanceof Error ? err.message : "",
            "No se pudo cargar el detalle de la orden."
          )
        );
      } finally {
        setDetailLoading(false);
      }
    },
    []
  );

  const clearDetail = useCallback(() => {
    setDetail(null);
    setDetailError(null);
    setDetailLoading(false);
  }, []);

  return {
    orders,
    loading,
    error,
    totalElements,
    filteredCount,
    filteredCountTruncated,
    filteredCountLoading,
    detail,
    detailLoading,
    detailError,
    fetchOrders,
    fetchOrdersForExport,
    fetchFilteredCount,
    openOrderDetail,
    clearDetail,
  };
};

export default useGetOrders;
